import { useState, useEffect } from "react";
import { settingsAPI, API_ORIGIN } from "../services/api";
import toast from "react-hot-toast";
import {
  HiOutlineGlobeAlt,
  HiOutlineEnvelope,
  HiOutlineHeart,
  HiOutlineShare,
  HiOutlineShoppingBag,
  HiOutlineDocumentText,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineClipboardDocument,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlinePaperAirplane,
  HiOutlineWrench,
  HiOutlineCreditCard,
} from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { useBrand } from "./brand";
import { useCurrentAdmin, hasFeature } from "./permissions";
import "./AdminSettings.css";

/**
 * Empty, on purpose.
 *
 * These used to be Forge's real values. On the Makua panel — whose settings
 * row starts empty — the form showed Forge's organisation, address and
 * mail server, and saving would have written them in. That matters more
 * than it looks: getSmtpConfig() reads this table IN PREFERENCE to the
 * SMTP_* environment variables, so one Save would have had Makua trying to
 * send as noreply@forge.ngo through mail.forge.ngo, and every send would
 * be rejected.
 *
 * The organisation fields are prefilled from whichever brand this panel is
 * pointed at. The mail fields stay blank, which means "use the server's
 * own SMTP_* settings" — the thing that is already working.
 */
const INITIAL = {
  orgName: "",
  contactEmail: "",
  phone: "",
  address: "",
  smtpHost: "",
  smtpPort: "587",
  smtpUser: "",
  smtpPass: "",
  fromEmail: "",
  fromName: "",
  stripePublic: "",
  stripeSecret: "",
  stripeWebhook: "",
  currency: "USD",
  enableDonations: true,
  facebook: "",
  instagram: "",
  linkedin: "",
  discord: "",
  privacyUrl: "",
  tosUrl: "",
  shippingFlatRate: "0",
  collectNote: "",
};

// Sentinel value the API returns when a password is already saved
const MASK = "••••••••";

function SettingInput({ label, value, onChange, type = "text", textarea = false, rows = 3, monospace = false, placeholder = "" }) {
  const [showPw, setShowPw] = useState(false);
  const isPassword = type === "password";

  // True when the field holds the API mask — means "password is saved, not editable yet"
  const isMasked = isPassword && value === MASK;

  // When user clicks the field while masked, clear it so they can type a new one
  const handleFocus = () => {
    if (isMasked) onChange("");
  };

  return (
    <div className="aset__field">
      <label className="aset__label">{label}</label>

      {isPassword && isMasked ? (
        // ── Locked state: password is saved, show placeholder chip ──
        <div className="aset__pw-saved">
          <div className="aset__pw-saved-left">
            <span className="aset__pw-saved-dots">••••••••••••</span>
            <span className="aset__pw-saved-label">Saved</span>
          </div>
          <button type="button" className="aset__pw-change-btn" onClick={() => onChange("")}>
            Change
          </button>
        </div>
      ) : (
        // ── Edit state: user is entering / has entered a new password ──
        <div className="aset__input-wrap">
          {textarea ? (
            <textarea className="aset__input aset__textarea" rows={rows} value={value} onChange={(e) => onChange(e.target.value)} onFocus={handleFocus} placeholder={placeholder} />
          ) : (
            <input
              type={isPassword && !showPw ? "password" : "text"}
              className={`aset__input ${monospace ? "aset__input--mono" : ""}`}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onFocus={handleFocus}
              placeholder={isPassword ? "Enter new password…" : placeholder}
              autoComplete={isPassword ? "new-password" : undefined}
            />
          )}
          {isPassword && (
            <button type="button" className="aset__pw-toggle" onClick={() => setShowPw((v) => !v)} title={showPw ? "Hide password" : "Show password"}>
              {showPw ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button type="button" className={`aset__toggle ${checked ? "aset__toggle--on" : ""}`} onClick={() => onChange(!checked)} role="switch" aria-checked={checked}>
      <span className="aset__toggle-thumb" />
    </button>
  );
}

function SectionCard({ Icon, title, children, badge }) {
  return (
    <div className="acard aset__section">
      <div className="aset__section-head">
        <Icon className="aset__section-icon" aria-hidden="true" />
        <h3 className="aset__section-title">{title}</h3>
        {badge && <span className="aset__section-badge">{badge}</span>}
      </div>
      <div className="aset__section-body">{children}</div>
    </div>
  );
}

/* ── SMTP Test Panel ── */
function SmtpTestPanel({ form }) {
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, message }

  const runTest = async () => {
    if (!testTo) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await settingsAPI.testEmail(testTo);
      const msg = res.message || `Test email sent to ${testTo}`;
      setTestResult({ ok: true, message: msg });
      toast.success(msg);
    } catch (err) {
      const msg = err.message || "SMTP test failed";
      setTestResult({ ok: false, message: msg });
      toast.error("SMTP test failed: " + msg, { duration: 8000 });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="aset__smtp-test">
      <div className="aset__smtp-test-head">
        <HiOutlineWrench className="aset__smtp-test-icon" />
        <div>
          <p className="aset__smtp-test-title">Test SMTP Connection</p>
          <p className="aset__smtp-test-sub">Send a test email to verify your SMTP settings are working correctly.</p>
        </div>
      </div>

      <div className="aset__smtp-test-row">
        <div className="aset__input-wrap" style={{ flex: 1 }}>
          <input
            type="email"
            className="aset__input"
            placeholder="Enter email to send test to…"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runTest()}
          />
        </div>
        <button type="button" className="a-btn a-btn--rust" onClick={runTest} disabled={testing || !testTo} style={{ flexShrink: 0 }}>
          <HiOutlinePaperAirplane />
          {testing ? "Sending…" : "Send Test"}
        </button>
      </div>

      {testResult && (
        <div className={`aset__smtp-result ${testResult.ok ? "aset__smtp-result--ok" : "aset__smtp-result--err"}`}>
          {testResult.ok ? <HiOutlineCheckCircle className="aset__result-icon" /> : <HiOutlineXCircle className="aset__result-icon" />}
          <span>{testResult.message}</span>
        </div>
      )}

      <div className="aset__smtp-info">
        <p className="aset__smtp-info-label">Current configuration</p>
        <div className="aset__smtp-info-grid">
          <span>Host</span>
          <strong>{form.smtpHost || "—"}</strong>
          <span>Port</span>
          <strong>{form.smtpPort || "—"}</strong>
          <span>User</span>
          <strong>{form.smtpUser || "—"}</strong>
          <span>From</span>
          <strong>{form.fromEmail || "—"}</strong>
          <span>Auth</span>
          <strong>{form.smtpPass ? "Password set ✓" : "No password set"}</strong>
        </div>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const brand = useBrand();
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { admin } = useCurrentAdmin();
  const showDonations = hasFeature(admin, "donations");
  const showShop = hasFeature(admin, "shop");
  // Makua takes card payments for reservations; Forge's keys live under Donations.
  const showCards = !showDonations && !!admin?.features?.reservations;

  /* A blank organisation name means nothing has been saved yet, so show
     this deployment's own brand rather than an empty box. Never touched
     once a value exists, and never applied to the mail fields. */
  useEffect(() => {
    if (!loaded) return;
    setForm((prev) => ({
      ...prev,
      orgName: prev.orgName || brand.name,
      contactEmail: prev.contactEmail || brand.supportEmail,
      fromName: prev.fromName || brand.name,
    }));
  }, [loaded, brand.name, brand.supportEmail]);

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        if (res?.data) {
          const d = res.data;
          setForm((prev) => ({
            ...prev,
            orgName: d.organization_name || prev.orgName,
            contactEmail: d.contact_email || prev.contactEmail,
            phone: d.phone || prev.phone,
            address: d.address || prev.address,
            smtpHost: d.smtp_host || prev.smtpHost,
            smtpPort: d.smtp_port || prev.smtpPort,
            smtpUser: d.smtp_username || prev.smtpUser,
            smtpPass: d.smtp_password || prev.smtpPass,
            fromEmail: d.from_email || prev.fromEmail,
            fromName: d.from_name || prev.fromName,
            stripePublic: d.stripe_publishable_key || prev.stripePublic,
            stripeSecret: d.stripe_secret_key || prev.stripeSecret,
            stripeWebhook: d.stripe_webhook_secret || prev.stripeWebhook,
            currency: d.currency || prev.currency,
            enableDonations: d.enable_donations === 1 || d.enable_donations === true,
            facebook: d.facebook || prev.facebook,
            instagram: d.instagram || prev.instagram,
            linkedin: d.linkedin || prev.linkedin,
            discord: d.discord || prev.discord,
            privacyUrl: d.privacy_url || prev.privacyUrl,
            tosUrl: d.tos_url || prev.tosUrl,
            shippingFlatRate: d.shipping_flat_rate != null ? String(d.shipping_flat_rate) : prev.shippingFlatRate,
            collectNote: d.shop_collect_note || prev.collectNote,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const set = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update({
        organization_name: form.orgName,
        contact_email: form.contactEmail,
        phone: form.phone,
        address: form.address,
        smtp_host: form.smtpHost,
        smtp_port: form.smtpPort,
        smtp_username: form.smtpUser,
        // Only send password if user typed a new one (not empty, not the mask)
        smtp_password: form.smtpPass && form.smtpPass !== MASK ? form.smtpPass : undefined,
        from_email: form.fromEmail,
        from_name: form.fromName,
        stripe_publishable_key: form.stripePublic,
        stripe_secret_key: form.stripeSecret && form.stripeSecret !== MASK && !form.stripeSecret.startsWith("sk_live_••") ? form.stripeSecret : undefined,
        stripe_webhook_secret: showCards && form.stripeWebhook && form.stripeWebhook !== MASK ? form.stripeWebhook : undefined,
        currency: form.currency,
        enable_donations: form.enableDonations,
        facebook: form.facebook,
        instagram: form.instagram,
        linkedin: form.linkedin,
        discord: form.discord,
        privacy_url: form.privacyUrl,
        tos_url: form.tosUrl,
        // Only sent where there's a shop; the backend ignores them elsewhere.
        shipping_flat_rate: showShop ? form.shippingFlatRate : undefined,
        shop_collect_note: showShop ? form.collectNote : undefined,
      });
      toast.success("Settings saved successfully.");
    } catch (err) {
      toast.error(err.message || "Save failed. Check your connection.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout pageTitle="Settings" pageSubtitle="Manage your organization settings." permission="settings.view">
      {/* 3-col top */}
      <div className="aset__top-grid">
        {/* Organization */}
        <SectionCard Icon={HiOutlineGlobeAlt} title="Organization Information">
          <SettingInput label="Organization Name" value={form.orgName} onChange={set("orgName")} placeholder="Forge Retreat Colombia" />
          <SettingInput label="Contact Email" value={form.contactEmail} onChange={set("contactEmail")} placeholder="info@forge.ngo" />
          <SettingInput label="Phone Number" value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" />
          <SettingInput label="Address" value={form.address} onChange={set("address")} textarea rows={4} placeholder="123 Street, City, State" />
        </SectionCard>

        {/* Email SMTP */}
        <SectionCard Icon={HiOutlineEnvelope} title="Email (SMTP) Settings" badge="Mailcow / Google Workspace">
          <p className="aset__note">
            Leave these blank to keep using the mail settings configured on the server.
            Anything entered here overrides them — and a From address the mailbox
            doesn't own will have every message rejected.
          </p>
          <div className="aset__smtp-hint">
            <p>
              <strong>Mailcow (current):</strong> use <code>mail.forge.ngo</code>, port <code>587</code>, your mailbox credentials.
              <br />
              <strong>Google Workspace:</strong> use <code>smtp.gmail.com</code>, port <code>587</code>, your Google app password.
            </p>
          </div>
          <SettingInput label="SMTP Host" value={form.smtpHost} onChange={set("smtpHost")} placeholder="mail.forge.ngo" />
          <SettingInput label="SMTP Port" value={form.smtpPort} onChange={set("smtpPort")} placeholder="587" />
          <SettingInput label="SMTP Username" value={form.smtpUser} onChange={set("smtpUser")} placeholder="noreply@forge.ngo" />
          <SettingInput label="SMTP Password" value={form.smtpPass} onChange={set("smtpPass")} type="password" placeholder="Your mailbox or app password" />
          <SettingInput label="From Email" value={form.fromEmail} onChange={set("fromEmail")} placeholder="noreply@forge.ngo" />
          <SettingInput label="From Name" value={form.fromName} onChange={set("fromName")} placeholder="Forge Retreat Colombia" />

          {/* SMTP Test */}
          <SmtpTestPanel form={form} />
        </SectionCard>

        {/* Card payments for reservations (Makua) */}
        {showCards && (
        <SectionCard Icon={HiOutlineCreditCard} title="Card payments (Stripe)">
          <p className="aset__note">
            Guests pay deposits, instalments and balances on Stripe's own secure page. Use this
            organisation's own Stripe account: money for Makua and Forge never mixes.
          </p>
          <SettingInput label="Stripe Publishable Key" value={form.stripePublic} onChange={set("stripePublic")} placeholder="pk_live_…" monospace />
          <SettingInput label="Stripe Secret Key" value={form.stripeSecret} onChange={set("stripeSecret")} type="password" placeholder="sk_live_…" />
          <SettingInput label="Webhook Signing Secret" value={form.stripeWebhook} onChange={set("stripeWebhook")} type="password" placeholder="whsec_…" />
          <p className="aset__hint">
            In Stripe → Developers → Webhooks, add the endpoint <code>{`${API_ORIGIN}/api/stripe/webhook`}</code> with the events
            {" "}<code>checkout.session.completed</code>, <code>checkout.session.async_payment_succeeded</code> and <code>charge.refunded</code>,
            then paste its signing secret above. Card shows to guests once the secret key is saved and Card is ticked in Booking settings.
          </p>
        </SectionCard>
        )}

        {/* Donations */}
        {showDonations && (
        <SectionCard Icon={HiOutlineHeart} title="Donation Settings">
          <SettingInput label="Stripe Publishable Key" value={form.stripePublic} onChange={set("stripePublic")} placeholder="pk_live_…" monospace />
          <SettingInput label="Stripe Secret Key" value={form.stripeSecret} onChange={set("stripeSecret")} type="password" placeholder="sk_live_…" />
          <div className="aset__field">
            <label className="aset__label">Currency</label>
            <div className="aset__select-wrap">
              <select className="aset__select" value={form.currency} onChange={(e) => set("currency")(e.target.value)}>
                <option value="USD">USD – US Dollar</option>
                <option value="EUR">EUR – Euro</option>
                <option value="GBP">GBP – British Pound</option>
                <option value="COP">COP – Colombian Peso</option>
              </select>
            </div>
          </div>
          <div className="aset__toggle-row">
            <div>
              <p className="aset__toggle-label">Enable Donations</p>
              <p className="aset__toggle-sub">Allow donations on the website.</p>
            </div>
            <Toggle checked={form.enableDonations} onChange={set("enableDonations")} />
          </div>
        </SectionCard>
        )}
      </div>

      {/* 2-col bottom */}
      <div className="aset__bottom-grid">
        {showShop && (
        <SectionCard Icon={HiOutlineShoppingBag} title="Shop">
          <div className="aset__social-grid">
            <div className="aset__social-row">
              <span className="aset__social-label">Shipping (flat rate)</span>
              <input
                className="aset__input aset__social-input"
                type="number" min={0} step="0.01"
                value={form.shippingFlatRate}
                onChange={(e) => set("shippingFlatRate")(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="aset__social-row">
              <span className="aset__social-label">Collection note</span>
              <input
                className="aset__input aset__social-input"
                value={form.collectNote}
                onChange={(e) => set("collectNote")(e.target.value)}
                placeholder="Collect from us at Cerro Tusa when you arrive."
              />
            </div>
          </div>
          <p className="aset__hint">
            One shipping charge per order. Set it to 0 to post for free — collection never adds it.
          </p>
        </SectionCard>
        )}

        <SectionCard Icon={HiOutlineShare} title="Social Links">
          <div className="aset__social-grid">
            {[
              ["Facebook", "facebook"],
              ["Instagram", "instagram"],
              ["LinkedIn", "linkedin"],
              ["Discord Invite", "discord"],
            ].map(([label, key]) => (
              <div key={key} className="aset__social-row">
                <span className="aset__social-label">{label}</span>
                <input className="aset__input aset__social-input" value={form[key]} onChange={(e) => set(key)(e.target.value)} placeholder={`https://`} />
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard Icon={HiOutlineDocumentText} title="Legal Links">
          <div className="aset__social-grid">
            {[
              ["Privacy Policy URL", "privacyUrl"],
              ["Terms of Service URL", "tosUrl"],
            ].map(([label, key]) => (
              <div key={key} className="aset__social-row">
                <span className="aset__social-label">{label}</span>
                <input className="aset__input aset__social-input" value={form[key]} onChange={(e) => set(key)(e.target.value)} placeholder="https://forge.ngo/…" />
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Save bar */}
      <div className="aset__save-bar">
        <button type="button" className="a-btn a-btn--rust aset__save-btn" onClick={handleSave} disabled={saving}>
          <HiOutlineClipboardDocument />
          {saving ? "Saving…" : "Save Changes"}
        </button>

        <p className="aset__save-hint">Changes apply immediately to all outgoing emails and features.</p>
      </div>
    </AdminLayout>
  );
}
